import { useAuth } from "@/providers";
import {
  type CollectionRecord,
  type CollectionResourceType,
} from "@/shared/domain/collections";
import { useCollectionsQuery } from "@/shared/domain/collections/hooks/useCollectionsQuery";
import { Combobox } from "@/shared/foundation/primitives";

export interface CollectionPickerProps {
  /** The chosen collection's id, or null. */
  value: string | null;
  onValueChange: (collectionId: string | null) => void;
  /** Offer only collections holding this member type. */
  resourceType?: CollectionResourceType;
  /** A visible label; omit it when a heading names the picker instead. */
  label?: string;
  /** Id of the heading that names the picker when it has no label of its own. */
  "aria-labelledby"?: string;
  disabled?: boolean;
}

function collectionLabel(collection: CollectionRecord): string {
  return typeof collection.name === "string" && collection.name.length > 0
    ? `${collection.name} (${collection.collection_id})`
    : collection.collection_id;
}

/**
 * One collection out of the org's, searchable, most recently changed first.
 *
 * A collection the query has not returned yet — a stored id, or one past the
 * first page — still shows as its own id rather than as an empty field.
 *
 * It has no field of its own: put it in a `Field.Root`, which it reads for its
 * invalid state, when it needs an error line.
 */
export function CollectionPicker({
  value,
  onValueChange,
  resourceType,
  label,
  "aria-labelledby": ariaLabelledBy,
  disabled,
}: CollectionPickerProps) {
  const { currentOrganization } = useAuth();
  const collectionsQuery = useCollectionsQuery({
    orgId: currentOrganization?.org_id,
    resourceType,
    sortBy: "updated",
  });
  const collections = collectionsQuery.data?.items ?? [];
  const known = collections.some(
    (collection) => collection.collection_id === value,
  );
  const items =
    value === null || known
      ? collections
      : [
          { collection_id: value, name: null } as CollectionRecord,
          ...collections,
        ];

  return (
    <Combobox<CollectionRecord>
      items={items}
      value={
        items.find((collection) => collection.collection_id === value) ?? null
      }
      onValueChange={(collection) => {
        onValueChange(collection === null ? null : collection.collection_id);
      }}
      itemToStringLabel={collectionLabel}
      isItemEqualToValue={(item, chosen) =>
        item.collection_id === chosen.collection_id
      }
      label={label}
      disabled={disabled}
      size="lg"
    >
      <Combobox.TriggerInput
        placeholder="Search collections"
        aria-labelledby={ariaLabelledBy}
      />
      <Combobox.Content>
        <Combobox.Empty />
        <Combobox.List>
          {(collection: CollectionRecord) => (
            <Combobox.Item key={collection.collection_id} value={collection} />
          )}
        </Combobox.List>
      </Combobox.Content>
    </Combobox>
  );
}
